import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  caseManagements: [],
  currentCaseManagement: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Case Managements
export const fetchCaseManagements = createAsyncThunk(
  'caseManagements/fetchCaseManagements',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/case-managements/${patientId}` : '/case-managements';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch case management records'
      );
    }
  }
);

// Fetch a single Case Management record
export const fetchCaseManagement = createAsyncThunk(
  'caseManagements/fetchCaseManagement',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/case-managements/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch case management record'
      );
    }
  }
);

// Create a new Case Management record
export const createCaseManagement = createAsyncThunk(
  'caseManagements/createCaseManagement',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/case-managements', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create case management record'
      );
    }
  }
);

// Update an existing Case Management record
export const updateCaseManagement = createAsyncThunk(
  'caseManagements/updateCaseManagement',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/case-managements/${id}`, formData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update case management record'
      );
    }
  }
);

// Delete a Case Management record
export const deleteCaseManagement = createAsyncThunk(
  'caseManagements/deleteCaseManagement',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/case-managements/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete case management record'
      );
    }
  }
);

// ==================== Slice ====================
const caseManagementSlice = createSlice({
  name: 'caseManagements',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentCaseManagement: (state, action) => {
      state.currentCaseManagement = action.payload;
    },
    clearCurrentCaseManagement: (state) => {
      state.currentCaseManagement = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchCaseManagements.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCaseManagements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.caseManagements = action.payload;
      })
      .addCase(fetchCaseManagements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchCaseManagement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCaseManagement.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCaseManagement = action.payload;
      })
      .addCase(fetchCaseManagement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createCaseManagement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCaseManagement.fulfilled, (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.caseManagements.unshift(...action.payload);
        } else {
          state.caseManagements.unshift(action.payload);
        }
      })
      .addCase(createCaseManagement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateCaseManagement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCaseManagement.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const index = state.caseManagements.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.caseManagements[index] = updated;
        }
        if (state.currentCaseManagement?.id === updated.id) {
          state.currentCaseManagement = updated;
        }
      })
      .addCase(updateCaseManagement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteCaseManagement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCaseManagement.fulfilled, (state, action) => {
        state.isLoading = false;
        state.caseManagements = state.caseManagements.filter(
          (d) => d.id !== action.payload
        );
        if (state.currentCaseManagement?.id === action.payload) {
          state.currentCaseManagement = null;
        }
      })
      .addCase(deleteCaseManagement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentCaseManagement,
  clearCurrentCaseManagement,
} = caseManagementSlice.actions;

export default caseManagementSlice.reducer;
